import React, { useEffect, useState, useRef } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useWallet } from '../../context/WalletContext';
import { useNotify } from '../../context/NotificationContext';
import { useMachineDisplay } from '../../hooks/useMachineDisplay';
import { useXtPrice } from '../../hooks/useXtPrice';
import { usePackageTiers } from '../../hooks/usePackageTiers';
import { parseError } from '../../utils/ethers';
import { Spinner } from '../common/Loader';
import './MiningCounter.css';

const DEFAULT_ROI_SEC = 86400;
const TICK_MS = 100;

function splitNumber(n, fracDigits = 6) {
  if (!isFinite(n) || n < 0) n = 0;
  const parts = n.toFixed(fracDigits).split('.');
  return { whole: parts[0], fraction: parts[1] || '0'.repeat(fracDigits) };
}

/**
 * Live Mining Counter — per-user 24h gate.
 * Clock start = user.lastMiningClickTimestamp (if > 0)
 *             else earliest active package's purchaseTimestamp
 * Counter accrues from 0 → dailyTotal linearly across `roiTimeframe`.
 */
export default function MiningCounter({ userId, user, packages, onDone }) {
  const { read, write } = useContracts();
  const { account } = useWallet();
  const notify = useNotify();
  const { config: machineDisplay } = useMachineDisplay();
  // Shared reactive XT price — updates instantly when admin changes it.
  const { xtPrice } = useXtPrice();
  // Shared reactive tier list — MINING TODAY reads the current on-chain
  // dailyRewardUSDT via this so an admin edit propagates live.
  const { tiers } = usePackageTiers();

  const [now, setNow] = useState(Math.floor(Date.now() / 1000));
  const [roiSec, setRoiSec] = useState(DEFAULT_ROI_SEC);
  const [claiming, setClaiming] = useState(false);
  const tickRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    read.storage.roiTimeframe().then((roi) => {
      if (cancelled) return;
      if (roi && roi.gt(0)) setRoiSec(roi.toNumber());
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    tickRef.current = setInterval(() => {
      setNow(Date.now() / 1000);
    }, TICK_MS);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, []);

  const stats = computeAccrued(user, packages, now, xtPrice, roiSec, tiers);
  const whole = splitNumber(stats.xtAccrued, 6);
  const wholeUsdt = stats.usdtAccrued.toFixed(2);
  const progressPct = stats.progressPct;
  const secondsToReady = stats.secondsToReady;
  const ready = stats.ready;

  // Display ranges summed across all active packages (same source of truth):
  //   XT / cycle   = admin-configured min/max XT (per tier)
  //   USDT / cycle = same range × current on-chain XT price
  const xtDisplayRange = computeXtDisplayRange(packages, machineDisplay);
  const usdtDisplayRange = computeUsdtDisplayRange(packages, machineDisplay, xtPrice);

  async function handleClaim() {
    if (!write) {
      notify.error('Please connect your wallet on the correct network.');
      return;
    }
    if (!ready) {
      notify.warning('Mining is still accruing — please wait for the cycle to complete.');
      return;
    }
    setClaiming(true);
    try {
      // Preflight to surface real revert reason
      try {
        await write.logic.callStatic.claimMining();
      } catch (simErr) {
        const msg = parseError(simErr);
        if (msg.includes('TE')) throw new Error('Too early — cycle not yet complete.');
        if (msg.includes('NA')) throw new Error('No active mining machines.');
        if (msg.includes('NR')) throw new Error('You are not registered.');
        if (msg.includes('BL')) throw new Error('Your account is blacklisted.');
        if (msg.includes('PAUSED')) throw new Error('Mining is paused by admin.');

        const raw = String(simErr?.data || simErr?.error?.data || '');
        const noReason = !simErr?.reason && (raw === '0x' || raw === '' || msg === '' || msg === '0x');
        if (noReason) {
          throw new Error(
            'Mint failed. The Xterra contract likely needs minter permission on the XT token. ' +
            'Ask admin to grant it in Admin → Contracts.'
          );
        }
        throw simErr;
      }

      const tx = await write.logic.claimMining();
      notify.info('Mining transaction submitted...');
      await tx.wait();
      notify.success('Mining rewards harvested!');
      if (onDone) onDone();
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setClaiming(false);
    }
  }

  return (
    <div id="mining-counter" className="mining-rig">
      <div className="rig-bg" />
      <div className="rig-scan" />

      <div className="coin-swarm">
        <div className="coin" /><div className="coin" /><div className="coin" />
        <div className="coin" /><div className="coin" /><div className="coin" />
      </div>

      <div className="rig-header">
        <div className="rig-label">
          <span className="live-dot" />
          <span>MINING RIG · LIVE</span>
        </div>
        <div className="rig-eta">
          {stats.activeCount > 0
            ? <>Active machines: <strong>{stats.activeCount}</strong></>
            : <>No active machines</>
          }
        </div>
      </div>

      <div className="rig-body">
        <div className="rig-machine">
          <div className="rig-machine-ring rr1" />
          <div className="rig-machine-ring rr2" />
          <div className="rig-machine-ring rr3" />
          <div className="rig-machine-core">
            <div className="rig-machine-hex">
              <div className="rig-machine-icon">⛏</div>
            </div>
          </div>
          <div className="rig-machine-energy" />
        </div>

        <div className="rig-counter">
          <div className="rig-counter-label">MINING TODAY</div>
          <div className="rig-value">
            <span className="whole">{whole.whole}</span>
            <span className="fraction">.{whole.fraction}</span>
            <span className="digit-cursor" />
            <span className="currency">XT</span>
          </div>
          <div className="rig-usdt">
            ≈ <strong>{wholeUsdt} USDT</strong> accrued
          </div>
        </div>
      </div>

      <div className="rig-progress">
        <div className="rig-progress-track">
          <div className="rig-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <div className="rig-progress-labels">
          <span>{progressPct.toFixed(1)}% of {humanizeCycle(roiSec)} cycle</span>
          <span>
            {ready
              ? '⚡ Ready to harvest'
              : `Next in ${formatCountdown(secondsToReady)}`}
          </span>
        </div>
      </div>

      <div className="rig-stats">
        <div className="rig-stat">
          <div className="rig-stat-value">{stats.activeCount}</div>
          <div className="rig-stat-label">Machines</div>
        </div>
        <div className="rig-stat">
          <div className="rig-stat-value">{xtDisplayRange}</div>
          <div className="rig-stat-label">XT / Cycle</div>
        </div>
        <div className="rig-stat">
          <div className="rig-stat-value">{usdtDisplayRange}</div>
          <div className="rig-stat-label">USDT / Cycle</div>
        </div>
      </div>

      <div className="rig-footer">
        <button
          className={`rig-claim-btn ${ready ? 'ready' : ''}`}
          onClick={handleClaim}
          disabled={!ready || claiming || !account || stats.activeCount === 0}
        >
          {claiming
            ? <><Spinner /> MINING...</>
            : ready
              ? <>⛏ HARVEST MINING</>
              : stats.activeCount === 0
                ? <>NO ACTIVE MACHINES</>
                : <>ACCRUING...</>
          }
        </button>
        <div className={`rig-hint ${ready ? 'ready' : ''}`}>
          {ready
            ? `${humanizeCycle(roiSec)} cycle complete — click to harvest`
            : `One harvest every ${humanizeCycle(roiSec)} · Missed cycles are forfeited`}
        </div>
      </div>
    </div>
  );
}

function computeAccrued(user, packages, nowSec, xtPrice, roiSec, tiers) {
  const cycle = roiSec && roiSec > 0 ? roiSec : DEFAULT_ROI_SEC;
  const empty = {
    usdtAccrued: 0, xtAccrued: 0,
    usdtDailyMax: 0, xtDailyMax: 0,
    activeCount: 0, ready: false,
    progressPct: 0, secondsToReady: cycle,
  };

  if (!packages || packages.length === 0 || !user) return empty;

  const lastClick = user.lastMiningClickTimestamp ? Number(user.lastMiningClickTimestamp) : 0;
  let clockStart = lastClick;
  if (clockStart === 0) {
    let earliest = Number.MAX_SAFE_INTEGER;
    for (const pkg of packages) {
      if (!pkg.active) continue;
      const purchaseTs = Number(pkg.purchaseTimestamp);
      if (purchaseTs > 0 && purchaseTs < earliest) earliest = purchaseTs;
    }
    if (earliest === Number.MAX_SAFE_INTEGER) return empty;
    clockStart = earliest;
  }

  const elapsed = Math.max(0, nowSec - clockStart);
  const clamped = Math.min(elapsed, cycle);
  const ratio = clamped / cycle;
  const ready = elapsed >= cycle;
  const secondsToReady = ready ? 0 : Math.floor(cycle - elapsed);

  // MINING TODAY is driven by the on-chain dailyRewardUSDT. We prefer the
  // CURRENT tier value from usePackageTiers (so admin edits propagate live)
  // and fall back to the snapshot stored on the package if tiers aren't
  // loaded yet.
  let usdtDailyMax = 0;
  let activeCount = 0;
  for (const pkg of packages) {
    if (!pkg.active) continue;
    // New contract: no per-package cap; only click count limits mining
    if (Number(pkg.miningDaysClaimed) >= Number(pkg.miningDaysTotal)) continue;

    const tierIdx = Number(pkg.tierIndex);
    const currentTier = tiers && tiers[tierIdx] ? tiers[tierIdx] : null;
    const dailyBn = currentTier && currentTier.dailyRewardUSDT
      ? currentTier.dailyRewardUSDT
      : pkg.dailyRewardUSDT;
    const daily = Number(ethers.utils.formatUnits(dailyBn, 18));
    if (daily <= 0) continue;
    activeCount++;
    usdtDailyMax += daily;
  }

  const xtPriceNum = Number(ethers.utils.formatUnits(xtPrice, 18)) || 1;
  const xtDailyMax = usdtDailyMax / xtPriceNum;
  const usdtAccrued = usdtDailyMax * ratio;
  const xtAccrued = xtDailyMax * ratio;

  return {
    usdtAccrued, xtAccrued, usdtDailyMax, xtDailyMax,
    activeCount,
    ready: ready && activeCount > 0,
    progressPct: activeCount > 0 ? ratio * 100 : 0,
    secondsToReady,
  };
}

function formatCountdown(sec) {
  if (sec <= 0) return '00:00:00';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function humanizeCycle(sec) {
  const s = Number(sec) || 0;
  if (s === 0) return '—';
  if (s % 86400 === 0) return `${s / 86400}d`;
  if (s % 3600 === 0)  return `${s / 3600}h`;
  if (s % 60 === 0)    return `${s / 60}m`;
  return `${s}s`;
}

/**
 * Sum the frontend-configured XT display range across all active packages.
 * Returns a string like "1 - 2" or "6 - 12" or "0" if no active packages.
 */
function computeXtDisplayRange(packages, machineDisplay) {
  if (!packages || !packages.length || !machineDisplay) return '0';
  let sumMin = 0;
  let sumMax = 0;
  let any = false;
  for (const pkg of packages) {
    if (!pkg.active) continue;
    if (Number(pkg.miningDaysClaimed) >= Number(pkg.miningDaysTotal)) continue;
    const cfg = machineDisplay[Number(pkg.tierIndex)];
    if (!cfg) continue;
    const mn = Number(cfg.minXt);
    const mx = Number(cfg.maxXt);
    if (!isFinite(mn) || !isFinite(mx)) continue;
    sumMin += mn;
    sumMax += mx;
    any = true;
  }
  if (!any) return '0';
  const fmt = (n) => {
    if (n === Math.floor(n)) return String(n);
    return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };
  return `${fmt(sumMin)} - ${fmt(sumMax)}`;
}

/**
 * Same summed XT range as computeXtDisplayRange, converted to USDT at the
 * current on-chain XT price. Returns e.g. "1.00 - 5.00" or a single "1.00"
 * when min == max, or "0" when there are no active packages.
 */
function computeUsdtDisplayRange(packages, machineDisplay, xtPriceBn) {
  if (!packages || !packages.length || !machineDisplay) return '0';
  let sumMin = 0;
  let sumMax = 0;
  let any = false;
  for (const pkg of packages) {
    if (!pkg.active) continue;
    if (Number(pkg.miningDaysClaimed) >= Number(pkg.miningDaysTotal)) continue;
    const cfg = machineDisplay[Number(pkg.tierIndex)];
    if (!cfg) continue;
    const mn = Number(cfg.minXt);
    const mx = Number(cfg.maxXt);
    if (!isFinite(mn) || !isFinite(mx)) continue;
    sumMin += mn;
    sumMax += mx;
    any = true;
  }
  if (!any) return '0';

  let priceNum = 1;
  try {
    if (xtPriceBn && typeof xtPriceBn.toString === 'function') {
      priceNum = Number(xtPriceBn.toString()) / 1e18;
    } else if (typeof xtPriceBn === 'number') {
      priceNum = xtPriceBn;
    }
  } catch { priceNum = 1; }
  if (!isFinite(priceNum) || priceNum <= 0) priceNum = 1;

  const usdMin = sumMin * priceNum;
  const usdMax = sumMax * priceNum;
  const fmt = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return usdMin === usdMax ? fmt(usdMin) : `${fmt(usdMin)} - ${fmt(usdMax)}`;
}
